// The page of a locked cover (status "locked": a free user's Create, saved
// by POST /api/v2/conversion without running anything). Right after
// Create, the owner sees a short setup sequence in the cover loader's
// look, then the locked card and the paywall. Nothing has been made, so the
// copy says the cover starts once they subscribe, never that it is done.
// An owner who already has a plan gets "Make it now" (the unlock route);
// anyone else sees that it hasn't been made yet.
import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import axios from "axios";
import toast from "react-hot-toast";
import { getIdToken } from "../../utils/firebaseAuth";
import { Check, Lock, Music2 } from "lucide-react";
import { Spinner } from "@jammable/ui-components/Loader";
import { VoiceImage, voiceNames } from "./StudioChrome";
import LockedVideoPreview from "./LockedVideoPreview";
import {
  formatHoldLeft,
  isLockedExpired,
  lockedExpiresAt,
} from "@/utils/lockedCover";
import { useT } from "@jammable/ui-core/src/i18n";

/* eslint-disable @typescript-eslint/no-explicit-any */

// Message keys (messages/paywall.ts).
const STEPS = [
  "paywall.locked.step.upload",
  "paywall.locked.step.match",
  "paywall.locked.step.mix",
] as const;
// Where each stage ends, in percent: uneven on purpose (reading the audio is
// quick, the voice takes longest), like the real pipeline's stages.
const STAGE_ENDS = [17, 71, 100];

// One tick of the setup progress: small irregular jumps, the odd stall, and
// a slowdown near the end, so it never climbs at a constant rate. Returns
// [new percent, ms until the next tick]. Runs ~9-12s in total.
const nextTick = (pct: number): [number, number] => {
  if (Math.random() < 0.08) return [pct, 600 + Math.random() * 700];
  const near = pct > 88 ? 0.35 : pct > 70 ? 0.7 : 1;
  const jump = (1 + Math.random() * 5) * near;
  return [Math.min(100, pct + jump), 80 + Math.random() * 300];
};
// The setup sequence only plays straight after Create, not on a revisit.
const FRESH_MS = 2 * 60 * 1000;

// The real cover loader's ring (packages/ui/features/conversion Loader).
const RING = 300;
const STROKE = 14;
const RADIUS = (RING - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

// The cover's name is the song title, or the machine title of a generated
// song or text to speech ("Generate: ...", "TTS: ...").
const songTitle = (t: ReturnType<typeof useT>, name?: string) =>
  !name
    ? t("paywall.locked.yourAudio")
    : name.startsWith("Generate:")
      ? t("paywall.locked.yourAiSong")
      : name.startsWith("TTS:")
        ? t("paywall.locked.yourWords")
        : name;

const LockedCover = ({
  conversion,
  isOwner,
  hasPlan,
  onPaywall,
}: {
  conversion: any;
  isOwner: boolean;
  hasPlan: boolean;
  // Why it opened, for the Paywall events: "auto" (the card just locked
  // after Create), "unlock" (they tapped Unlock), "out-of-credits".
  onPaywall: (trigger: string) => void;
}) => {
  const router = useRouter();
  const t = useT();
  const voices: any[] = conversion?.models || [];
  const fresh =
    isOwner &&
    Date.now() - new Date(conversion?.createdAt || 0).getTime() < FRESH_MS;
  const [pct, setPct] = useState(fresh ? 0 : 100);
  const [starting, setStarting] = useState(false);
  const ready = pct >= 100;
  const step = ready ? STEPS.length : STAGE_ENDS.findIndex((end) => pct < end);

  // One self-scheduling timer for the whole run. (Scheduling off the pct
  // state stalled for good: a stall tick sets the same value, React skips
  // the re-render, and the next tick was never scheduled.)
  useEffect(() => {
    if (!fresh) return;
    let current = 0;
    let t: ReturnType<typeof setTimeout>;
    const tick = () => {
      const [next, wait] = nextTick(current);
      t = setTimeout(() => {
        current = next;
        setPct(next);
        if (next < 100) tick();
      }, wait);
    };
    tick();
    return () => clearTimeout(t);
  }, [fresh]);

  // The paywall opens once, as the card locks after Create: the moment
  // they've just made something.
  useEffect(() => {
    if (ready && fresh && !hasPlan) onPaywall("auto");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const makeNow = async () => {
    setStarting(true);
    try {
      const token = await getIdToken();
      const { data } = await axios.post(
        `/api/v2/conversion/unlock/${conversion.shortid}`,
        {},
        { headers: { Authorization: token } },
      );
      router.replace(`/conversion/${data.shortid}`);
    } catch (err: any) {
      setStarting(false);
      const message = err?.response?.data?.message;
      if (message === "You are out of credits") onPaywall("out-of-credits");
      else toast.error(message || t("paywall.locked.startFailed"));
    }
  };

  const names = voiceNames(voices) || t("paywall.locked.yourVoice");
  const song = songTitle(t, conversion?.name);
  // "Held for {time}": the text either side of the styled countdown.
  const held = t("paywall.heldFor");
  const heldAt = held.indexOf("{time}");
  const heldBefore = heldAt < 0 ? held : held.slice(0, heldAt);
  const heldAfter = heldAt < 0 ? "" : held.slice(heldAt + "{time}".length);
  const plural = voices.length > 1;
  const progress = pct / 100;
  const firstVoice = voices[0];

  // The 48h hold (enforced by the unlock route): a live countdown, then an
  // expired state that sends them back to make it again.
  const holdEnds = lockedExpiresAt(conversion?.createdAt);
  const expired = isLockedExpired(conversion?.createdAt);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!ready || expired) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [ready, expired]);

  const createHref = firstVoice?.urlParam
    ? `/create?voice=${encodeURIComponent(firstVoice.urlParam)}`
    : "/create";
  const n = Math.min(voices.length, 4);
  const avatarSize =
    n <= 2
      ? "tw-h-[96px] tw-w-[96px] md:tw-h-[120px] md:tw-w-[120px]"
      : n === 3
        ? "tw-h-[80px] tw-w-[80px] md:tw-h-[96px] md:tw-w-[96px]"
        : "tw-h-[64px] tw-w-[64px] md:tw-h-[76px] md:tw-w-[76px]";

  // Laid out exactly like the real cover loader (two columns: the ring
  // panel, then badge / title / stages), so a locked cover reads as the
  // same product as a cover being made.
  return (
    <section className="tw-grid tw-items-center tw-gap-6 tw-pb-16 tw-pt-2 md:tw-grid-cols-2 md:tw-gap-10">
      <div className="tw-relative tw-flex tw-h-[340px] tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-[34px] tw-bg-jam-lav md:tw-h-[560px] md:tw-rounded-[44px]">
        <svg
          width={RING}
          height={RING}
          viewBox={`0 0 ${RING} ${RING}`}
          className={`tw-absolute tw-h-[260px] tw-w-[260px] tw-transition-opacity tw-duration-500 md:tw-h-[300px] md:tw-w-[300px] ${ready || (isOwner && !expired) ? "tw-hidden" : ""}`}
          style={{ transform: "rotate(-90deg)" }}
          aria-hidden
        >
          <circle
            cx={RING / 2}
            cy={RING / 2}
            r={RADIUS}
            fill="none"
            stroke="#E3D3F7"
            strokeWidth={STROKE}
          />
          <circle
            cx={RING / 2}
            cy={RING / 2}
            r={RADIUS}
            fill="none"
            stroke="#782DC8"
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={CIRCUMFERENCE * (1 - progress)}
            style={{ transition: "stroke-dashoffset 0.3s ease-out" }}
          />
        </svg>
        {/* The owner sees their video drawing itself in while it sets up,
          then locked; anyone else (or an expired cover) sees the voices. */}
        {isOwner && !expired ? (
          <LockedVideoPreview
            voices={voices}
            title={song}
            onUnlock={hasPlan ? makeNow : () => onPaywall("unlock")}
            cta={hasPlan ? t("paywall.locked.makeNow") : t("paywall.unlock")}
            progress={progress}
          />
        ) : (
          <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-gap-4">
            <div className="tw-relative tw-flex">
              {voices.slice(0, 4).map((v, i) => (
                <span
                  key={v._id || i}
                  style={{ marginLeft: i ? -24 : 0 }}
                  className={`tw-relative tw-overflow-hidden tw-rounded-full tw-border-[5px] tw-border-solid tw-border-white tw-bg-jam-line ${avatarSize}`}
                >
                  <VoiceImage
                    model={v}
                    fill
                    sizes="120px"
                    className="tw-object-cover"
                  />
                </span>
              ))}
              {ready && (
                <span className="tw-absolute tw--bottom-1 tw--right-3 tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-border-4 tw-border-solid tw-border-jam-lav tw-bg-jam-ink tw-text-jam-lime md:tw-h-14 md:tw-w-14">
                  <Lock size={22} strokeWidth={2.75} />
                </span>
              )}
            </div>
            {ready && (
              <span className="tw-flex tw-max-w-[280px] tw-items-center tw-gap-2 tw-rounded-full tw-bg-white tw-px-4 tw-py-2.5 tw-text-sm tw-font-extrabold tw-text-jam-ink">
                <Music2
                  size={16}
                  strokeWidth={2.5}
                  className="tw-shrink-0 tw-text-jam-purple"
                />
                <span className="tw-truncate">{song}</span>
              </span>
            )}
          </div>
        )}
      </div>

      <div className="tw-flex tw-flex-col tw-gap-5">
        {!ready ? (
          <>
            <span className="tw-self-start tw-rounded-full tw-bg-[#FFF6CC] tw-px-3 tw-py-1.5 tw-text-[13px] tw-font-extrabold tw-tabular-nums tw-text-[#7A6400]">
              {t("paywall.locked.pctDone", { pct: Math.floor(pct) })}
            </span>
            <h1 className="jam-display tw-m-0 tw-text-[34px] tw-font-extrabold tw-leading-none tw-tracking-[-0.045em] tw-text-jam-ink md:tw-text-[52px]">
              {t("paywall.locked.gettingReady", { names, song })}
            </h1>
            <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
              {STEPS.map((label, i) => {
                const done = i < step;
                const now = i === step;
                return (
                  <li
                    key={label}
                    className={`tw-flex tw-items-center tw-gap-3.5 tw-rounded-[20px] tw-border-2 tw-border-solid tw-px-4 tw-py-3 ${
                      now
                        ? "tw-border-jam-purple tw-bg-white"
                        : done
                          ? "tw-border-jam-lav tw-bg-jam-lav"
                          : "tw-border-jam-line tw-bg-white"
                    }`}
                  >
                    <span
                      className={`tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-[3px] tw-border-solid ${
                        done
                          ? "tw-border-jam-lime tw-bg-jam-lime"
                          : now
                            ? "tw-border-jam-purple tw-bg-white"
                            : "tw-border-jam-line tw-bg-white"
                      }`}
                    >
                      {done && (
                        <Check
                          size={14}
                          strokeWidth={3}
                          className="tw-text-jam-ink"
                        />
                      )}
                    </span>
                    <span
                      className={`tw-flex-grow tw-text-[15px] tw-font-extrabold ${done || now ? "tw-text-jam-ink" : "tw-text-jam-muted"}`}
                    >
                      {t(label)}
                    </span>
                    <span className="tw-text-xs tw-font-extrabold tw-text-jam-muted">
                      {done
                        ? t("paywall.locked.done")
                        : now
                          ? t("paywall.locked.now")
                          : ""}
                    </span>
                  </li>
                );
              })}
            </ol>
          </>
        ) : isOwner && expired ? (
          <>
            <h1 className="jam-display tw-m-0 tw-text-[34px] tw-font-extrabold tw-leading-none tw-tracking-[-0.045em] tw-text-jam-ink md:tw-text-[52px]">
              {t("paywall.locked.expired")}
            </h1>
            <p className="tw-m-0 tw-text-base tw-font-semibold tw-text-jam-muted">
              {t("paywall.locked.expiredBody")}
            </p>
            <button
              type="button"
              onClick={() => router.push(createHref)}
              className="tw-flex tw-h-[64px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-purple tw-text-[18px] tw-font-extrabold tw-text-white"
            >
              {t("paywall.locked.makeAgain")}
            </button>
          </>
        ) : isOwner ? (
          <>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              <span className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-jam-ink tw-px-3 tw-py-1.5 tw-text-[13px] tw-font-extrabold tw-text-jam-lime">
                <Lock size={13} strokeWidth={3} />
                {t("paywall.locked.readyToMake")}
              </span>
              <span className="tw-rounded-full tw-bg-[#FFF6CC] tw-px-3 tw-py-1.5 tw-text-[13px] tw-font-extrabold tw-text-[#7A6400]">
                {heldBefore}
                <span className="tw-tabular-nums">
                  {formatHoldLeft(holdEnds)}
                </span>
                {heldAfter}
              </span>
            </div>
            <h1 className="jam-display tw-m-0 tw-text-[34px] tw-font-extrabold tw-leading-none tw-tracking-[-0.045em] tw-text-jam-ink md:tw-text-[52px]">
              {plural
                ? t("paywall.locked.readyMany", { names, song })
                : t("paywall.locked.readyOne", { names, song })}
            </h1>
            <p className="tw-m-0 tw-text-base tw-font-semibold tw-text-jam-muted">
              {hasPlan
                ? t("paywall.locked.onPlan")
                : t("paywall.locked.unlockBody")}
            </p>
            <button
              type="button"
              onClick={hasPlan ? makeNow : () => onPaywall("unlock")}
              disabled={starting}
              className="tw-flex tw-h-[64px] tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-jam-lime tw-text-[18px] tw-font-extrabold tw-text-jam-ink tw-transition-colors hover:tw-bg-[#C4EE00] disabled:tw-cursor-wait"
            >
              {starting ? (
                <Spinner className="tw-border-3 tw-border-jam-ink" size={4} />
              ) : hasPlan ? (
                t("paywall.locked.makeNow")
              ) : (
                <>
                  <Lock size={18} strokeWidth={2.75} />
                  {t("paywall.unlockMyCover")}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => router.push(createHref)}
              className="tw-cursor-pointer tw-self-start tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-bold tw-text-jam-muted hover:tw-text-jam-purple"
            >
              {t("paywall.locked.change")}
            </button>
          </>
        ) : (
          <>
            <h1 className="jam-display tw-m-0 tw-text-[34px] tw-font-extrabold tw-leading-none tw-tracking-[-0.045em] tw-text-jam-ink md:tw-text-[52px]">
              {t("paywall.locked.notMade")}
            </h1>
            <p className="tw-m-0 tw-text-base tw-font-semibold tw-text-jam-muted">
              {t("paywall.locked.makeYourOwn", { names })}
            </p>
            <button
              type="button"
              onClick={() => router.push(createHref)}
              className="tw-flex tw-h-[64px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-purple tw-text-[18px] tw-font-extrabold tw-text-white"
            >
              {t("paywall.locked.makeCover")}
            </button>
          </>
        )}
      </div>
    </section>
  );
};

export default LockedCover;
